import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Key } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";

interface MFAVerificationProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export function MFAVerification({ onSuccess, onCancel }: MFAVerificationProps) {
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code || code.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a 6-digit code from your authenticator app.",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    
    try {
      const response = await fetch("/api/mfa/verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ code }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: "Verification Successful",
          description: data.usedBackupCode 
            ? "Logged in using backup code. Please regenerate your backup codes."
            : "MFA verification successful.",
        });
        
        queryClient.invalidateQueries({ queryKey: ["/api/user"] });
        onSuccess();
      } else {
        toast({
          title: "Verification Failed",
          description: data.error || "Invalid MFA code. Please try again.",
          variant: "destructive",
        });
        setCode("");
      }
    } catch (error: any) {
      toast({
        title: "Verification Error",
        description: error.message || "Failed to verify MFA code.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Card className="shadow-xl border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
      <CardHeader className="space-y-1 text-center pb-4">
        <div className="flex items-center justify-center mb-4">
          <div className="p-3 bg-purple-500 rounded-xl shadow-lg">
            <Shield className="h-8 w-8 text-white" />
          </div>
        </div>
        <CardTitle className="text-2xl font-semibold text-slate-900 dark:text-white">
          Multi-Factor Authentication
        </CardTitle>
        <CardDescription className="text-slate-600 dark:text-slate-400">
          Enter the 6-digit code from your authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleVerify} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mfa-code" className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Verification Code
            </Label>
            <div className="relative">
              <Key className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                id="mfa-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="pl-10 h-12 bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-center text-2xl tracking-widest"
                required
                autoFocus
              />
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              You can also use a backup code if you don't have access to your authenticator app.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
              disabled={isVerifying}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isVerifying || code.length !== 6}
              className="flex-1 bg-gradient-purple hover:opacity-90 text-white"
            >
              {isVerifying ? "Verifying..." : "Verify"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}


















